import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import { Dialog, TextField, IconButton, Box, Typography } from '@mui/material';

import { PrimaryButton } from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	ICreateEditSiteFormData,
	ICreateSiteModalProps,
} from 'src/features/site/domain/types';
import { CreateEditSiteValidationSchema } from 'src/features/site/domain/validation';
import {
	SITE_LOADING_KEY,
	createSite,
	updateSite,
} from 'src/features/site/store';
import { AppDispatch } from 'src/store';

export const CreateEditSiteModal: React.FC<ICreateSiteModalProps> = ({
	open,
	onClose,
	onSubmit,
	initialSiteData,
}) => {
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[SITE_LOADING_KEY],
	);
	const dispatch = useDispatch<AppDispatch>();

	const {
		control,
		handleSubmit,
		formState: { errors },
		reset,
	} = useForm<ICreateEditSiteFormData>({
		resolver: yupResolver(CreateEditSiteValidationSchema),
	});

	const handleSave = async (data: ICreateEditSiteFormData) => {
		const siteData = {
			name: data.siteName,
			description: data.description,
		};

		if (initialSiteData) {
			if (
				JSON.stringify(siteData) ===
				JSON.stringify({
					name: initialSiteData.name,
					description: initialSiteData.description,
				})
			) {
				onClose();
				return;
			}

			dispatch(
				updateSite({
					request: siteData,
					siteSlug: initialSiteData.slug,
					onSuccess: () => {
						onSubmit?.(data);
						snackbar('Site updated successfully', 'success');
					},
				}),
			);
		} else {
			dispatch(
				createSite({
					request: siteData,
					onSuccess: () => {
						onSubmit?.(data);
						snackbar('Site created successfully', 'success');
					},
				}),
			);
		}
	};

	useEffect(() => {
		if (open) {
			reset({
				siteName: initialSiteData?.name || '',
				description: initialSiteData?.description || '',
			});
		}
	}, [open, initialSiteData, reset]);

	if (!open) return null;

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth='sm'
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: '8px',
					padding: '24px',
					maxWidth: '610px',
					margin: '0 auto',
				},
			}}
			sx={{
				'& .MuiDialog-paper': {
					margin: '0 auto',
				},
				'& .MuiBackdrop-root': {
					backgroundColor: 'rgba(0, 0, 0, 0.5)',
				},
			}}
		>
			<Box sx={{ maxWidth: '552px', width: '100%', margin: '0 auto' }}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'flex-start',
						width: '100%',
						minHeight: '49px',
					}}
				>
					<Typography
						variant='h6'
						component='h2'
						sx={{
							fontSize: '20px',
							fontWeight: 500,
							lineHeight: 1.5,
						}}
					>
						{initialSiteData ? 'Edit site' : 'Create new site'}
					</Typography>
					<IconButton
						aria-label='close'
						onClick={onClose}
						sx={{
							padding: 0,
							'&:hover': {
								backgroundColor: 'transparent',
							},
						}}
					>
						<CloseIcon sx={{ fontSize: '25px' }} />
					</IconButton>
				</Box>

				<form onSubmit={handleSubmit(handleSave)}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
						}}
					>
						<Controller
							name='siteName'
							control={control}
							defaultValue=''
							render={({ field }) => (
								<TextField
									placeholder='Site Name'
									fullWidth
									size='medium'
									{...field}
									error={!!errors?.siteName}
									helperText={errors?.siteName?.message}
									variant='outlined'
								/>
							)}
						/>
						<Controller
							name='description'
							control={control}
							defaultValue=''
							render={({ field }) => (
								<TextField
									placeholder='Description'
									fullWidth
									multiline
									rows={4}
									size='medium'
									{...field}
									error={!!errors?.description}
									helperText={errors?.description?.message}
									variant='outlined'
								/>
							)}
						/>
						<PrimaryButton
							type='submit'
							isLoading={isLoading}
							sx={{
								width: '100%',
								marginTop: '8px',
							}}
						>
							{initialSiteData ? 'SAVE CHANGES' : 'CREATE'}
						</PrimaryButton>
					</Box>
				</form>
			</Box>
		</Dialog>
	);
};
